import { readFileText, setupMarkdownToolPage } from './shared/index.js';
import {
  CSV_MAX_BYTES,
  validateCsvInput,
} from './formats/csv/convert.js';
import CsvWorker from './formats/csv/worker.js?worker';

const WORKER_TIMEOUT_MS = 30_000;

function sourceEditor(root) {
  return root.querySelector('#source-text, #csv-source, textarea[name="source"]');
}

function findControl(root, selectors) {
  return root.querySelector(selectors);
}

function runWorker(WorkerConstructor, payload, signal) {
  if (typeof Worker === 'undefined') return Promise.reject(new Error('This browser cannot start the local conversion worker.'));
  if (signal?.aborted) return Promise.reject(new DOMException('Conversion canceled.', 'AbortError'));
  return new Promise((resolve, reject) => {
    const worker = new WorkerConstructor({ name: 'kivoza-csv-to-markdown' });
    let settled = false;
    let timeoutId;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', cancel);
      worker.terminate();
      callback(value);
    };
    const cancel = () => finish(reject, new DOMException('Conversion canceled.', 'AbortError'));
    timeoutId = setTimeout(() => finish(reject, new Error('CSV conversion timed out after 30 seconds.')), WORKER_TIMEOUT_MS);
    signal?.addEventListener('abort', cancel, { once: true });
    worker.onmessage = (event) => {
      if (event.data?.ok) finish(resolve, event.data.result);
      else finish(reject, new Error(event.data?.error || 'CSV conversion failed.'));
    };
    worker.onerror = (event) => finish(reject, new Error(event.message || 'CSV conversion worker failed.'));
    worker.postMessage(payload);
  });
}

export function initCsvPage(root = document) {
  const source = sourceEditor(root);
  const delimiter = findControl(root, '#csv-delimiter, #delimiter, select[name="delimiter"]');
  const hasHeader = findControl(root, '#csv-has-header, #has-header, input[name="hasHeader"]');
  const maxRows = findControl(root, '#csv-max-rows, input[name="maxRows"]');
  const trim = findControl(root, '#csv-trim, input[name="trim"]');
  let sourceFile = null;
  let reading = false;
  const page = setupMarkdownToolPage({
    root,
    defaultFileName: 'csv-document',
    onFiles: async (files) => {
      const file = files?.[0];
      if (!file) return;
      if (files.length > 1) { page.setStatus('Choose one CSV file at a time.', 'error'); return; }
      if (!/\.csv$/i.test(file.name) && file.type && !/(?:csv|plain|text)/i.test(file.type)) {
        page.setStatus('Choose a CSV file.', 'error');
        return;
      }
      if (reading) return;
      page.setReady(false);
      reading = true;
      try {
        const text = await readFileText(file, CSV_MAX_BYTES, 'CSV file');
        sourceFile = file;
        if (source) source.value = text;
        page.setReady(true);
        page.setStatus(`${file.name} is ready. Choose the delimiter and header option.`, 'success');
      } catch (error) {
        sourceFile = null;
        page.setStatus(error instanceof Error ? error.message : 'The CSV file could not be read.', 'error');
      } finally {
        reading = false;
      }
    },
  });
  const convertButton = page.elements.convertButton;
  convertButton?.addEventListener('click', () => {
    const text = source?.value || '';
    const options = {
      delimiter: delimiter?.value || ',',
      hasHeader: hasHeader ? Boolean(hasHeader.checked) : true,
      maxRows: maxRows?.value || 10_000,
      trim: trim ? Boolean(trim.checked) : true,
    };
    const problem = validateCsvInput(text, options);
    if (problem) { page.setStatus(problem, 'error'); return; }
    page.result.clear();
    page.runConversion(async ({ signal, report }) => {
      report('Parsing CSV locally…', 0.2);
      const result = await runWorker(CsvWorker, { source: text, options }, signal);
      report('Markdown is ready.', 1);
      return result;
    }).catch(() => {});
  });
  page.elements.clearButton?.addEventListener('click', () => {
    sourceFile = null;
    if (source) source.value = '';
    page.setReady(false);
    page.result.clear();
    page.setStatus('Source cleared.');
  });
  source?.addEventListener('input', () => {
    sourceFile = null;
    const options = { delimiter: delimiter?.value || ',', maxRows: maxRows?.value || 10_000 };
    page.setReady(!validateCsvInput(source.value, options));
  });
  page.setReady(Boolean(source?.value?.trim()) && !validateCsvInput(source.value, { delimiter: delimiter?.value || ',', maxRows: maxRows?.value || 10_000 }));
  return { page, get sourceFile() { return sourceFile; } };
}

if (typeof document !== 'undefined' && document.documentElement.dataset.markdownFormat === 'csv') initCsvPage();
